/**
 * Ring buffer of the last few failed API responses this browser saw, for the
 * in-app support request (IASR).
 *
 * Module-level state rather than React state on purpose: the failures worth
 * reporting happen in the request layer, outside any component tree, and a
 * support request filed minutes later still has to see them. The buffer is
 * capped so it can never grow into the outbound email — `apiErrors` in the
 * contract accepts at most 5 items.
 *
 * Only the method, path and status are kept. Request and response bodies are
 * never captured: this content leaves the platform in an email to support, and
 * bodies here carry compensation data and PII.
 */
import type { SupportRequestApiError } from 'types';
import { toPathOnly } from './requestPath';

const MAX_API_ERRORS = 5;
const MAX_METHOD_LENGTH = 10;
const MAX_PATH_LENGTH = 500;

const apiErrors: SupportRequestApiError[] = [];

type CaptureApiErrorInput = {
  method: string;
  /** Full URL or root-relative path — origin and query string are stripped. */
  url: string;
  status: number;
};

/**
 * Record a failed API response. Oldest entry is evicted once the buffer is full.
 *
 * A status outside the contract's 100–599 range is dropped rather than stored:
 * a synthetic status (0 from an aborted request, undefined from a client-side
 * throw) would fail payload validation at submit time and cost the user their
 * whole support request over an entry that says nothing.
 */
export function captureApiError({ method, url, status }: CaptureApiErrorInput): void {
  if (!Number.isInteger(status) || status < 100 || status > 599) {
    return;
  }

  apiErrors.push({
    at: new Date().toISOString(),
    method: method.slice(0, MAX_METHOD_LENGTH),
    path: toPathOnly(url).slice(0, MAX_PATH_LENGTH),
    status,
  });

  if (apiErrors.length > MAX_API_ERRORS) {
    apiErrors.shift();
  }
}

/** The captured failures, oldest first, as the contract expects them. */
export function getApiErrors(): SupportRequestApiError[] {
  return [...apiErrors];
}

/** Test-only: the buffer outlives any single test otherwise. */
export function resetApiErrors(): void {
  apiErrors.length = 0;
}
